import React, { useState } from 'react';
import { Alert, Anchor, Button, PasswordInput, Stack, Text } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconExclamationCircle } from '@tabler/icons-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AuthLayout from './AuthLayout';
import { apiErrorMessage } from '../../data/apiClient';
import { resetPassword } from '../../data/PasswordAuth';
import { useCsrf } from '../../hooks/useCsrf';

/**
 * Consume a reset link. Both the token and the email arrive as query params,
 * put there by ResetPassword::createUrlUsing() on the backend.
 */
const ResetPasswordForm = () => {
    const { csrfToken } = useCsrf();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [done, setDone] = useState(false);

    const token = searchParams.get('token') ?? '';
    const email = searchParams.get('email') ?? '';

    const form = useForm({
        initialValues: { password: '', confirm: '' },
        validate: {
            // Mirrors Laravel's Password::defaults() (min 8) on the API side.
            password: value => (value.length >= 8 ? null : 'Use at least 8 characters'),
            confirm: (value, values) => (value === values.password ? null : 'Passwords do not match'),
        },
    });

    const handleSubmit = async (values: typeof form.values) => {
        setSaving(true);
        setError('');
        try {
            await resetPassword(csrfToken, {
                token,
                email,
                password: values.password,
                password_confirmation: values.confirm,
            });
            setDone(true);
        } catch (err: any) {
            setError(apiErrorMessage(err, 'Could not reset your password.'));
        } finally {
            setSaving(false);
        }
    };

    const heading = (text: string) => (
        <Text
            style={{
                fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
                lineHeight: 1.1,
                fontWeight: 700,
                color: 'var(--foreground)',
            }}
        >
            {text}
        </Text>
    );

    if (!token || !email) {
        return (
            <AuthLayout>
                <Stack gap="lg">
                    {heading('Reset password')}
                    <Alert variant="light" color="red" icon={<IconExclamationCircle size={18} />}>
                        This reset link is incomplete. Request a new one.
                    </Alert>
                    <Anchor onClick={() => navigate('/recover-password')} style={{ fontWeight: 600 }}>
                        Request a new link
                    </Anchor>
                </Stack>
            </AuthLayout>
        );
    }

    if (done) {
        return (
            <AuthLayout>
                <Stack gap="lg">
                    {heading('Password updated')}
                    <Text style={{ color: 'var(--foreground-secondary)' }}>
                        You can sign in with your new password now.
                    </Text>
                    <Button size="md" radius={9999} onClick={() => navigate('/sign-in')}>
                        Go to sign in
                    </Button>
                </Stack>
            </AuthLayout>
        );
    }

    return (
        <AuthLayout>
            <Stack gap="lg">
                {heading('Set a new password')}
                <Text style={{ color: 'var(--foreground-secondary)' }}>
                    Resetting the password for <strong>{email}</strong>.
                </Text>

                {error && (
                    <Alert variant="light" color="red" icon={<IconExclamationCircle size={18} />}>
                        {error}
                    </Alert>
                )}

                <form onSubmit={form.onSubmit(handleSubmit)}>
                    <Stack gap="sm">
                        <PasswordInput
                            label="New password"
                            autoComplete="new-password"
                            disabled={saving}
                            {...form.getInputProps('password')}
                        />
                        <PasswordInput
                            label="Confirm new password"
                            autoComplete="new-password"
                            disabled={saving}
                            {...form.getInputProps('confirm')}
                        />
                        <Button type="submit" size="md" radius={9999} loading={saving}>
                            Save password
                        </Button>
                    </Stack>
                </form>
            </Stack>
        </AuthLayout>
    );
};

export default ResetPasswordForm;
